/**
 * Every error the automation hits, traced (user, 19/10/2026: "every send
 * error — everything has to be traced, every error sent on Slack and
 * visualised on the automation"). One row per error: where it happened
 * (country, container, gateway, drop), the exact message, what the
 * automation did next, whether an alert went out, and whether it resolved
 * itself (a refused drop that a later attempt sent). Kinds are fixed words;
 * readers tolerate an unknown one.
 * For Media Devoted | Created By Ciro Autiero
 */

export const DROP_GEN_ERROR_KINDS = [
  "send_failed",
  "send_unknown",
  "autosend_halted",
  "autosend_failed",
  "schedule_failed",
  "generate_failed",
  "creative_failed",
  "verdict_failed",
  "scaling_failed",
  "hit_again_failed",
  "tracker_unreadable",
  "stats_failed",
  "tags_unreadable",
  "ranking_failed",
  "bookkeeping_failed",
  "notification_failed",
  "scheduler_failed",
  "experiments_failed",
  "backfill_failed",
] as const;
export type DropGenErrorKind = (typeof DROP_GEN_ERROR_KINDS)[number];

/** critical = sends stop or a person must act · error = a step failed, the automation goes on · warning = a read failed, judged with less. */
export type DropGenErrorTone = "critical" | "error" | "warning";

export const DROP_GEN_ERROR_WORDS: Record<
  DropGenErrorKind,
  { label: string; hint: string; tone: DropGenErrorTone; fix: string }
> = {
  send_failed: {
    label: "Send refused",
    hint: "SMSWiser refused the send. The drop stays a draft and auto-send tries again at the next tick; N refusals in a row halt auto-send.",
    tone: "error",
    fix: "Read what SMSWiser said — usually the route is off for the account, the sender is not allowed, or the list is empty. Fix it in SMSWiser; auto-send tries the drop again each minute.",
  },
  send_unknown: {
    label: "Send outcome unknown",
    hint: "The send threw while the draft was already SENDING — it may have gone out. Auto-send halts; check the drop in SMSWiser, then Resume.",
    tone: "critical",
    fix: "Open SMSWiser and check whether this drop went out, then press Resume on the country page and answer sent or not sent.",
  },
  autosend_halted: {
    label: "Auto-send halted",
    hint: "A technical failure halted auto-send for the country. Nothing goes out until a person presses Resume.",
    tone: "critical",
    fix: "Read the reason, fix it, then press Resume on the country page — nothing goes out until then.",
  },
  autosend_failed: {
    label: "Auto-send pass failed",
    hint: "The scheduled auto-send pass itself failed — no drop of this minute was judged. The next minute tries again.",
    tone: "critical",
    fix: "Nothing to do if it happens once — the next minute tries again. If it repeats, send this message to the developers.",
  },
  schedule_failed: {
    label: "Schedule not written",
    hint: "SMSWiser's schedule could not be written: a scheduled drop, the plan's re-sync, or a route-test winner.",
    tone: "error",
    fix: "Check the drop in SMSWiser’s scheduled list. The next sync tries again; if it keeps failing, set Delivery timing to Keep local until due.",
  },
  generate_failed: {
    label: "Day not laid out",
    hint: "The day could not be generated, or one slot could not be synced into a draft.",
    tone: "critical",
    fix: "Read the reason (often a list or campaign missing in SMSWiser), fix it, then press Generate now.",
  },
  creative_failed: {
    label: "Texts not filled",
    hint: "The AI texts or senders of some drafts could not be filled. Those drafts wait for texts; auto-send holds them.",
    tone: "error",
    fix: "Swap the prompt group for another one on this container (or its master), or open it in Creative AI and ask for shorter texts — one SMS is 160 plain characters, no emoji — then fill the texts again. The error turns Resolved once its drafts have their texts.",
  },
  verdict_failed: {
    label: "Verdict failed",
    hint: "The verdict pass, or applying one of its decisions (a burn, a stop, a route-test winner), failed.",
    tone: "error",
    fix: "Nothing to do — the next verdict pass (every 15 minutes) judges again. If it repeats, send this message to the developers.",
  },
  scaling_failed: {
    label: "Scaling failed",
    hint: "A scaling step or the re-sync after it failed; the next drops keep their amount.",
    tone: "error",
    fix: "Nothing to do — the next pass tries again; the next drops keep their amount meanwhile.",
  },
  hit_again_failed: {
    label: "Hit again failed",
    hint: "A hit-again drop could not be laid.",
    tone: "error",
    fix: "Nothing to do — the next pass judges the drop again.",
  },
  tracker_unreadable: {
    label: "Tracker unreadable",
    hint: "The tracker's report could not be read: verdicts and rankings judged with SMSWiser's numbers only, or inconclusive.",
    tone: "warning",
    fix: "Check the campaign's tracker is up and the campaign exists there. The next pass reads again; verdicts use SMSWiser’s numbers meanwhile.",
  },
  stats_failed: {
    label: "Stats unreadable",
    hint: "SMSWiser's stats of a sent drop could not be read; the verdict sees the drop without them.",
    tone: "warning",
    fix: "Nothing to do — the next pass reads the stats again.",
  },
  tags_unreadable: {
    label: "Tags unreadable",
    hint: "A rule's tags could not be read; the rule judged without them.",
    tone: "warning",
    fix: "Check the campaign’s tags in the tracker; rules that test a tag judge without it meanwhile.",
  },
  ranking_failed: {
    label: "Ranking failed",
    hint: "A ranking could not be read or warmed; the next read tries again.",
    tone: "warning",
    fix: "Nothing to do — the next read tries again.",
  },
  bookkeeping_failed: {
    label: "Bookkeeping failed",
    hint: "A runtime write failed — sent facts, a cursor, an audit row. The send or edit itself succeeded.",
    tone: "error",
    fix: "The action itself worked. If the screen looks wrong, press Refresh; if it repeats, send this message to the developers.",
  },
  notification_failed: {
    label: "Alert not delivered",
    hint: "Slack or Telegram did not take the alert. Traced here; not re-sent to the channel that failed.",
    tone: "error",
    fix: "Open Notifications (the bell on the country page), check the Slack webhook and thread, and send a test.",
  },
  scheduler_failed: {
    label: "Scheduler failed",
    hint: "The automation's heartbeat failed this minute — every country's steps of that minute were skipped.",
    tone: "critical",
    fix: "If it repeats, the server needs a look — send this message to the developers.",
  },
  experiments_failed: {
    label: "Experiments failed",
    hint: "The experiments' judge pass failed; the running experiments keep running.",
    tone: "error",
    fix: "Nothing to do — the experiments keep running; the next pass judges them.",
  },
  backfill_failed: {
    label: "Backfill failed",
    hint: "A one-off data backfill failed at start-up.",
    tone: "error",
    fix: "Send this message to the developers — a one-off data update did not finish at start-up.",
  },
};

/** The sources, as the trace names them. */
export type DropGenErrorSource =
  | "AUTOSEND"
  | "SEND"
  | "GENERATE"
  | "CREATIVE"
  | "VERDICT"
  | "SCHEDULER"
  | "TRACKER"
  | "NOTIFY"
  | "SYSTEM";

/** The country of a system-wide error (the scheduler's heartbeat, a backfill). */
export const DROP_GEN_SYSTEM_COUNTRY = "*";

export interface DropGenErrorRow {
  id: string;
  /** First time (ISO). */
  at: string;
  /** Last repeat (ISO) — equals `at` unless `repeats` > 1. */
  lastAt: string;
  repeats: number;
  /** "*" = system-wide. */
  countryKey: string;
  kind: string;
  source: string;
  headline: string;
  message: string;
  containerId: string | null;
  containerName: string | null;
  groupId: string | null;
  routeId: number | null;
  routeName: string | null;
  dropId: string | null;
  /** The drop's send time (ISO). */
  dropAt: string | null;
  listName: string | null;
  /** YYYY-MM-DD. */
  runDate: string | null;
  attempt: number | null;
  /** What the automation did next, in words. */
  next: string | null;
  notifiedAt: string | null;
  notifyError: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  detail: Record<string, unknown> | null;
}

/** Whether every error reaches Slack for this country, and why not. */
export interface DropGenErrorsSlackState {
  on: boolean;
  why: string | null;
  lastSentAt: string | null;
  lastError: string | null;
}

export interface DropGenErrorsReport {
  countryKey: string;
  /** Run days read, today included. */
  days: number;
  /** YYYY-MM-DD, inclusive, on the country's clock. */
  from: string;
  to: string;
  rows: DropGenErrorRow[];
  byKind: Array<{ kind: string; total: number; today: number }>;
  /** One per run day in the range, oldest first. */
  byDay: Array<{ date: string; total: number; byKind: Record<string, number> }>;
  /** Today's errors not resolved. */
  open: number;
  slack: DropGenErrorsSlackState;
}

/** The index page's strip: today's errors per country. */
export interface DropGenErrorsSummary {
  at: string;
  countries: Array<{
    countryKey: string;
    today: number;
    open: number;
    critical: number;
    lastAt: string | null;
  }>;
  system: { today: number; lastAt: string | null };
}
